import React from 'react'
import {
  ComposedChart, BarChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { ChartCard, TooltipBox } from './AIChartParts'
import { GRID, AXIS_TEXT, num, compact, shortDate, longDate } from './aiCostFormat'

// Gemini API usage per day, laid out like Google AI Studio > Usage (data: GET /ai-cost/gemini/usage)

const SUCCESS = '#1aa3a3'
const FAILED = '#d64a8e'
const RATE = '#2f9e44'
const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#8e5bd6', '#d6a12a', '#8a8984']
const colorAt = (i) => PALETTE[i % PALETTE.length]

const axisProps = { tick: { fill: AXIS_TEXT, fontSize: 12 }, axisLine: false, tickLine: false }
const xAxis = <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={16} {...axisProps} />
const legend = <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />

/** Tooltip listing every series of the hovered day; `format` per series name */
const DayTooltip = ({ active, payload, label, format = (v) => num(v) }) => {
  if (!active || !payload?.length) return null
  return (
    <TooltipBox
      title={longDate(label)}
      rows={payload.map((p) => ({ label: p.name, value: format(p.value, p.name), color: p.color }))}
    />
  )
}

const RequestsChart = ({ daily }) => (
  <ResponsiveContainer width="100%" height={260}>
    <ComposedChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
      <CartesianGrid vertical={false} stroke={GRID} />
      {xAxis}
      <YAxis yAxisId="req" tickFormatter={compact} width={44} allowDecimals={false} {...axisProps} />
      <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={44} {...axisProps} />
      <Tooltip
        cursor={{ fill: 'rgba(0,0,0,0.04)' }}
        content={<DayTooltip format={(v, name) => (name === 'Success rate' ? (v == null ? '-' : `${v}%`) : num(v))} />}
      />
      {legend}
      <Bar yAxisId="req" dataKey="success" name="Successful" stackId="req" fill={SUCCESS} maxBarSize={28} />
      <Bar yAxisId="req" dataKey="failed" name="Failed" stackId="req" fill={FAILED} radius={[4, 4, 0, 0]} maxBarSize={28} />
      <Line yAxisId="rate" dataKey="successRate" name="Success rate" stroke={RATE} strokeWidth={2} dot={false} connectNulls />
    </ComposedChart>
  </ResponsiveContainer>
)

const ErrorsChart = ({ daily, errors }) => (
  <ResponsiveContainer width="100%" height={260}>
    <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
      <CartesianGrid vertical={false} stroke={GRID} />
      {xAxis}
      <YAxis tickFormatter={compact} width={36} allowDecimals={false} {...axisProps} />
      <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<DayTooltip />} />
      {legend}
      {errors.map((error, i) => (
        <Bar key={error} dataKey={(d) => d.errors[error] || 0} name={error} stackId="err" fill={colorAt(i)} maxBarSize={20} />
      ))}
    </BarChart>
  </ResponsiveContainer>
)

/** One line per model; `field` is a key of daily[].models[model] */
const PerModelChart = ({ daily, models, field }) => (
  <ResponsiveContainer width="100%" height={220}>
    <LineChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid vertical={false} stroke={GRID} />
      {xAxis}
      <YAxis tickFormatter={compact} width={44} allowDecimals={false} {...axisProps} />
      <Tooltip content={<DayTooltip />} />
      {legend}
      {models.map((model, i) => (
        <Line
          key={model}
          dataKey={(d) => d.models[model]?.[field] || 0}
          name={model}
          stroke={colorAt(i)}
          strokeWidth={2}
          dot={false}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
)

const GeminiUsageSection = ({ usage, isLoading, error }) => {
  const daily = usage?.daily || []
  const models = usage?.models || []
  const errors = usage?.errors || []
  const state = { isLoading, error, isEmpty: !daily.some((d) => d.requests) }
  const totalRequests = daily.reduce((s, d) => s + d.requests, 0)
  const totalFailed = daily.reduce((s, d) => s + d.failed, 0)

  return (
    <>
      <div className="d-flex align-items-baseline gap-2 mt-4 mb-2">
        <h5 className="m-0">Gemini API Usage</h5>
        {!isLoading && !error && (
          <span className="text-muted small">
            {num(totalRequests)} requests · {num(totalFailed)} errors
          </span>
        )}
      </div>

      <div className="ai-cost-grid-2 ai-gemini-top">
        <ChartCard title="Total API Requests" subtitle="Successful and failed requests per day, with success rate" {...state}>
          <RequestsChart daily={daily} />
        </ChartCard>
        <ChartCard
          title="Total API Errors"
          subtitle="Failed requests per day by error"
          {...state}
          isEmpty={!errors.length}
          emptyText="No errors in this range"
        >
          <ErrorsChart daily={daily} errors={errors} />
        </ChartCard>
      </div>

      <div className="ai-cost-grid-3">
        <ChartCard title="Input Tokens per model" subtitle="Prompt tokens per day" {...state}>
          <PerModelChart daily={daily} models={models} field="inputTokens" />
        </ChartCard>
        <ChartCard title="Output Tokens per model" subtitle="Response tokens per day (excluding thinking)" {...state}>
          <PerModelChart daily={daily} models={models} field="outputTokens" />
        </ChartCard>
        <ChartCard title="Requests per model" subtitle="Requests per day" {...state}>
          <PerModelChart daily={daily} models={models} field="requests" />
        </ChartCard>
      </div>
    </>
  )
}

export default GeminiUsageSection
